import { getGoogleDriveStatus, disconnectGoogleDrive } from "@/modules/settings/requests/google-drive";
import {
  getJiraConnectUrl,
  getJiraStatus,
  disconnectJira,
} from "@/modules/settings/requests/jira";
import { ROUTES, SETTINGS_ROUTES } from "@/shared/constants/routes";
import requestConfig from "@/shared/config/request";
import { addNotification } from "@/shared/states/notification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import IntegrationCard from "./IntegrationCard";

const googleDriveApiBaseURL = requestConfig.baseUrl;

function IntegrationCardSkeleton() {
  return (
    <div className="flex flex-col p-5 rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] min-h-[220px] animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-[#E4E4E7]" />
        <div className="h-4 w-20 rounded bg-[#E4E4E7]" />
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <div className="h-3 w-full rounded bg-[#E4E4E7]" />
        <div className="h-3 w-3/4 rounded bg-[#E4E4E7]" />
      </div>
      <div className="mt-4 h-3 w-32 rounded bg-[#E4E4E7]" />
      <div className="mt-4 flex gap-2">
        <div className="h-8 w-20 rounded bg-[#E4E4E7]" />
        <div className="h-8 w-24 rounded bg-[#E4E4E7]" />
      </div>
    </div>
  );
}

export const JIRA_STATUS_QUERY_KEY = ["jira", "status"] as const;
export const GOOGLE_DRIVE_STATUS_QUERY_KEY = ["google-drive", "status"] as const;

export default function IntegrationsLanding() {
  const queryClient = useQueryClient();

  const { data: jiraStatus, isPending: isJiraPending } = useQuery({
    queryKey: JIRA_STATUS_QUERY_KEY,
    queryFn: getJiraStatus,
  });
  const { data: googleDriveStatus, isPending: isGoogleDrivePending } = useQuery({
    queryKey: GOOGLE_DRIVE_STATUS_QUERY_KEY,
    queryFn: getGoogleDriveStatus,
  });

  const isPending = isJiraPending || isGoogleDrivePending;

  const disconnectJiraMutation = useMutation({
    mutationFn: disconnectJira,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JIRA_STATUS_QUERY_KEY });
      addNotification({ type: "success", title: "Disconnected", message: "Jira disconnected." });
    },
    onError: (err) => {
      addNotification({
        type: "error",
        title: "Disconnect failed",
        message: axios.isAxiosError(err) ? String(err.response?.data?.detail ?? err.message) : "Failed.",
      });
    },
  });
  const disconnectGoogleDriveMutation = useMutation({
    mutationFn: disconnectGoogleDrive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOOGLE_DRIVE_STATUS_QUERY_KEY });
      addNotification({
        type: "success",
        title: "Disconnected",
        message: "Google Drive disconnected.",
      });
    },
    onError: (err) => {
      addNotification({
        type: "error",
        title: "Disconnect failed",
        message: axios.isAxiosError(err) ? String(err.response?.data?.detail ?? err.message) : "Failed.",
      });
    },
  });

  const handleConnectJira = async () => {
    try {
      const { authorize_url } = await getJiraConnectUrl();

      window.location.href = authorize_url;
    } catch (err) {
      addNotification({
        type: "error",
        title: "Failed to connect Jira",
        message: axios.isAxiosError(err)
          ? String(err.response?.data?.detail ?? err.message)
          : err instanceof Error
            ? err.message
            : "Unable to get Jira connect URL. Please try again.",
      });
    }
  };
  const handleConnectGoogleDrive = () => {
    try {
      window.location.href = `${googleDriveApiBaseURL}/v0/integrations/google-drive/connect/redirect`;
    } catch (err) {
      addNotification({
        type: "error",
        title: "Failed to connect Google Drive",
        message: err instanceof Error ? err.message : "Unable to start Google Drive connect. Please try again.",
      });
    }
  };

  const jiraConnected = jiraStatus?.connected === true;
  const googleDriveConnected = googleDriveStatus?.connected === true;

  return (
    <div className="p-6 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium leading-5 text-[#171717]">Integrations</h4>
        <p className="text-sm text-[#777780]">
          Connect external services to automate assessments and data imports.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isPending ? (
          <>
            <IntegrationCardSkeleton />
            <IntegrationCardSkeleton />
          </>
        ) : (
          <>
            <IntegrationCard
              logo="/jira-logo.svg"
              logoAlt="Jira"
              name="Jira"
              description="Create assessments and sync issues with Jira."
              connected={jiraConnected}
              connectedLabel={jiraStatus?.site_name ?? jiraStatus?.site_url}
              manageHref={`/${ROUTES.SETTINGS}/${SETTINGS_ROUTES.INTEGRATIONS_JIRA}`}
              onConnect={handleConnectJira}
              onDisconnect={jiraConnected ? () => disconnectJiraMutation.mutate() : undefined}
              isDisconnectPending={disconnectJiraMutation.isPending}
            />
            <IntegrationCard
              logo="/google-drive-logo.svg"
              logoAlt="Google Drive"
              name="Google Drive"
              description="Import assessment files from your Drive folder."
              connected={googleDriveConnected}
              connectedLabel={googleDriveStatus?.user_email}
              manageHref={`/${ROUTES.SETTINGS}/${SETTINGS_ROUTES.INTEGRATIONS_GOOGLE_DRIVE}`}
              onConnect={handleConnectGoogleDrive}
              onDisconnect={
                googleDriveConnected ? () => disconnectGoogleDriveMutation.mutate() : undefined
              }
              isDisconnectPending={disconnectGoogleDriveMutation.isPending}
            />
          </>
        )}
      </div>
    </div>
  );
}
